/*
 * Particle formations for the services field (field.js). Each formation fills
 * `out` (x, y, z, phase) for particle i of n. The phase (0..1) is what the
 * shader's flow / scan effects travel along. Shapes fit in a radius of ~2.1.
 *
 * Formations are built once, on the CPU, the first time a section needs them;
 * new ones go at the end of FORMATIONS so the seeds of the others don't change.
 */

// Seeded so every visit draws the same shapes
export function random(seed) {
    return () => {
        seed = (seed + 0x6d2b79f5) | 0;
        let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function gauss(rnd) {
    return Math.sqrt(-2 * Math.log(rnd() || 1e-6)) * Math.cos(2 * Math.PI * rnd());
}

function onSegment(out, a, b, t, jitter, rnd) {
    out[0] = a[0] + (b[0] - a[0]) * t + gauss(rnd) * jitter;
    out[1] = a[1] + (b[1] - a[1]) * t + gauss(rnd) * jitter;
    out[2] = a[2] + (b[2] - a[2]) * t + gauss(rnd) * jitter;
}

function fibonacci(i, n, r, out) {
    const y = 1 - 2 * (i + 0.5) / n;
    const s = Math.sqrt(1 - y * y);
    const a = i * 2.399963;
    out[0] = Math.cos(a) * s * r;
    out[1] = y * r;
    out[2] = Math.sin(a) * s * r;
}

function ring(out, radius, angle, tiltX, tiltZ, jitter, rnd) {
    const x = Math.cos(angle) * radius + gauss(rnd) * jitter;
    const z = Math.sin(angle) * radius + gauss(rnd) * jitter;
    const y = gauss(rnd) * jitter;
    // Tilt around X, then Z
    const y1 = y * Math.cos(tiltX) - z * Math.sin(tiltX);
    const z1 = y * Math.sin(tiltX) + z * Math.cos(tiltX);
    out[0] = x * Math.cos(tiltZ) - y1 * Math.sin(tiltZ);
    out[1] = x * Math.sin(tiltZ) + y1 * Math.cos(tiltZ);
    out[2] = z1;
}

/*
 * Each formation fills `out` (x, y, z, phase) for particle i of n. The phase
 * (0..1) is what the shader's flow / scan effects travel along.
 */
function near(out, p, jitter, rnd) {
    onSegment(out, p, p, 0, jitter, rnd);
}

// Picks segments ([a, b, phaseA?, phaseB?]) in proportion to their length, so
// long and short segments come out equally dense
function byLength(segments, rnd) {
    const sums = [];
    let total = 0;
    segments.forEach(([a, b]) => sums.push(total += Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2])));
    return () => {
        const r = rnd() * total;
        let lo = 0, hi = sums.length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (sums[mid] < r) lo = mid + 1;
            else hi = mid;
        }
        return segments[lo];
    };
}

// A point on a segment; its phase runs between the segment's end phases, or 0..1
function along(out, segment, jitter, rnd) {
    const t = rnd();
    onSegment(out, segment[0], segment[1], t, jitter, rnd);
    out[3] = segment.length > 2 ? segment[2] + (segment[3] - segment[2]) * t : t;
}

// The 12 edges of an axis-aligned box
function box(edges, [x0, y0, z0], [x1, y1, z1]) {
    const c = [
        [x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1],
        [x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1],
    ];
    [[0, 1], [1, 2], [2, 3], [3, 0], [4, 5], [5, 6], [6, 7], [7, 4], [0, 4], [1, 5], [2, 6], [3, 7]]
        .forEach(([a, b]) => edges.push([c[a], c[b]]));
    return edges;
}

// Closed outline through `points`
function loop(edges, points) {
    points.forEach((p, k) => edges.push([p, points[(k + 1) % points.length]]));
    return edges;
}

// Rectangle outline in the XY plane at depth z
function rect(edges, x0, y0, x1, y1, z) {
    return loop(edges, [[x0, y0, z], [x1, y0, z], [x1, y1, z], [x0, y1, z]]);
}

// Square outline in the XZ plane at height y
function rectXZ(edges, w, y) {
    return loop(edges, [[-w, y, -w], [w, y, -w], [w, y, w], [-w, y, w]]);
}

const TAU = Math.PI * 2;

export const FORMATIONS = {
    // Onesight Global: a globe with an orbit
    globe(n, rnd) {
        return (i, out) => {
            if (i < n * 0.78) {
                fibonacci(i, n * 0.78, 1.45, out);
                out[3] = (out[1] / 1.45 + 1) / 2;
            } else {
                const a = rnd() * Math.PI * 2;
                ring(out, 2.05 + gauss(rnd) * 0.03, a, 1.2, 0.35, 0.012, rnd);
                out[3] = a / (Math.PI * 2);
            }
        };
    },

    // AI: hubs wired to their nearest neighbours, shaped like a brain
    neural(n, rnd) {
        const hubs = Array.from({ length: 30 }, () => [
            gauss(rnd) * 0.75, gauss(rnd) * 0.5, gauss(rnd) * 0.5,
        ]);
        const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
        const edges = [];
        hubs.forEach((a, i) => {
            hubs
                .map((b, j) => [j, dist(a, b)])
                .filter(([j]) => j > i)
                .sort((x, y) => x[1] - y[1])
                .slice(0, 3)
                .forEach(([j]) => edges.push([a, hubs[j]]));
        });
        return (i, out) => {
            if (i < n * 0.3) {
                const hub = hubs[i % hubs.length];
                onSegment(out, hub, hub, 0, 0.055, rnd);
                out[3] = rnd();
            } else {
                const [a, b] = edges[Math.floor(rnd() * edges.length)];
                const t = rnd();
                onSegment(out, a, b, t, 0.012, rnd);
                out[3] = t;
            }
        };
    },

    // Engineering: an isometric block of server racks
    lattice(n, rnd) {
        const UNIT = 0.36;
        const heights = [4, 6, 3, 5, 8, 6, 2, 5, 4];
        const edges = [];
        heights.forEach((h, k) => {
            const x0 = ((k % 3) - 1.5) * UNIT * 2 + UNIT / 2;
            const z0 = (Math.floor(k / 3) - 1.5) * UNIT * 2 + UNIT / 2;
            const y0 = -4 * UNIT;
            for (let level = 0; level <= h; level++) {
                const y = y0 + level * UNIT;
                const c = [[x0, y, z0], [x0 + UNIT, y, z0], [x0 + UNIT, y, z0 + UNIT], [x0, y, z0 + UNIT]];
                for (let e = 0; e < 4; e++) edges.push([c[e], c[(e + 1) % 4]]);
            }
            [[0, 0], [UNIT, 0], [UNIT, UNIT], [0, UNIT]].forEach(([dx, dz]) => {
                edges.push([[x0 + dx, y0, z0 + dz], [x0 + dx, y0 + h * UNIT, z0 + dz]]);
            });
        });
        return (i, out) => {
            const [a, b] = edges[Math.floor(rnd() * edges.length)];
            onSegment(out, a, b, rnd(), 0.006, rnd);
            out[3] = (out[1] / UNIT + 4) / 8;
        };
    },

    // Cybersecurity: a faceted shield around a protected core, with a perimeter
    shield(n, rnd) {
        const t = (1 + Math.sqrt(5)) / 2;
        const v = [
            [-1, t, 0], [1, t, 0], [-1, -t, 0], [1, -t, 0],
            [0, -1, t], [0, 1, t], [0, -1, -t], [0, 1, -t],
            [t, 0, -1], [t, 0, 1], [-t, 0, -1], [-t, 0, 1],
        ].map(p => p.map(c => c / Math.hypot(1, t) * 1.45));
        const edges = [];
        for (let a = 0; a < v.length; a++) {
            for (let b = a + 1; b < v.length; b++) {
                const d = Math.hypot(v[a][0] - v[b][0], v[a][1] - v[b][1], v[a][2] - v[b][2]);
                if (d < 1.6) edges.push([v[a], v[b]]);
            }
        }
        return (i, out) => {
            if (i < n * 0.55) {
                onSegment(out, ...edges[Math.floor(rnd() * edges.length)], rnd(), 0.008, rnd);
            } else if (i < n * 0.78) {
                fibonacci(i - n * 0.55, n * 0.23, 0.62, out);
            } else {
                ring(out, 2.0, rnd() * Math.PI * 2, 1.38, 0, 0.01, rnd);
            }
            out[3] = (out[1] / 2 + 1) / 2;
        };
    },

    // Talent: people in orbit around a shared core
    orbits(n, rnd) {
        const rings = [[0.85, 1.1, 0.2], [1.35, 1.35, -0.45], [1.9, 1.2, 0.5]];
        return (i, out) => {
            if (i < n * 0.16) {
                onSegment(out, [0, 0, 0], [0, 0, 0], 0, 0.22, rnd);
                out[3] = rnd();
                return;
            }
            // Outer rings get more particles so they look as dense as inner ones
            const pick = rnd() * 4.1;
            const [r, tx, tz] = rings[pick < 0.85 ? 0 : pick < 2.2 ? 1 : 2];
            const a = rnd() * Math.PI * 2;
            ring(out, r, a, tx, tz, 0.018, rnd);
            out[3] = a / (Math.PI * 2);
        };
    },

    // Automation: one continuous synchronised loop (a 2,3 torus knot)
    flow(n, rnd) {
        return (i, out) => {
            const u = rnd();
            const a = u * Math.PI * 2;
            const r = 1.0 + 0.42 * Math.cos(3 * a);
            out[0] = r * Math.cos(2 * a) + gauss(rnd) * 0.035;
            out[1] = r * Math.sin(2 * a) + gauss(rnd) * 0.035;
            out[2] = 0.42 * Math.sin(3 * a) * 1.4 + gauss(rnd) * 0.035;
            out[3] = u;
        };
    },

    // Innovation: an open, rippling surface
    wave(n, rnd) {
        return (i, out) => {
            const x = (rnd() - 0.5) * 4.2;
            const z = (rnd() - 0.5) * 2.6;
            out[0] = x;
            out[1] = Math.sin(x * 1.6) * 0.22 + Math.cos(z * 2.1 + x * 0.6) * 0.18;
            out[2] = z;
            out[3] = (x / 4.2) + 0.5;
        };
    },

    // Design Impact Lab: a film strip with a half twist around a 3D play button
    reel(n, rnd) {
        const R = 1.55, W = 0.5, FRAMES = 30;
        const play = [[-0.3, 0.42], [-0.3, -0.42], [0.46, 0]];
        // Möbius strip lying in the XZ plane; the phase runs along the film
        function strip(out, u, v, jitter) {
            const r = R + v * Math.cos(u / 2);
            out[0] = r * Math.cos(u) + gauss(rnd) * jitter;
            out[1] = v * Math.sin(u / 2) + gauss(rnd) * jitter;
            out[2] = r * Math.sin(u) + gauss(rnd) * jitter;
            out[3] = u / (Math.PI * 2);
        }
        return (i, out) => {
            if (i < n * 0.12) {
                // Play button: front and back triangles of a shallow prism
                const e = Math.floor(rnd() * 3);
                const z = rnd() < 0.5 ? -0.1 : 0.1;
                const [a, b] = [play[e], play[(e + 1) % 3]];
                onSegment(out, [a[0], a[1], z], [b[0], b[1], z], rnd(), 0.008, rnd);
                out[3] = rnd();
            } else if (i < n * 0.46) {
                // Film edges
                strip(out, rnd() * Math.PI * 2, rnd() < 0.5 ? -W : W, 0.008);
            } else if (i < n * 0.64) {
                // Lines between frames
                strip(out, Math.floor(rnd() * FRAMES) / FRAMES * Math.PI * 2, (rnd() * 2 - 1) * W, 0.006);
            } else if (i < n * 0.8) {
                // Sprocket holes, three per frame on both sides
                const k = Math.floor(rnd() * FRAMES * 3) + 0.5;
                strip(out, k / (FRAMES * 3) * Math.PI * 2, (rnd() < 0.5 ? -0.82 : 0.82) * W, 0.012);
            } else {
                // The picture inside the frames
                strip(out, rnd() * Math.PI * 2, (rnd() * 2 - 1) * W * 0.65, 0.01);
            }
        };
    },

    // ── Service pages (src/js/services/looks.js) ────────────────────────────

    // AI · analytics: a 3D bar chart rising towards a forecast line
    bars(n, rnd) {
        const COLS = 6, ROWS = 4, W = 0.3, STEP = 0.5, FLOOR = -1.1;
        const edges = [];
        for (let r = 0; r < ROWS; r++) {
            for (let c = 0; c < COLS; c++) {
                const x = (c - (COLS - 1) / 2) * STEP;
                const z = (r - (ROWS - 1) / 2) * STEP;
                const h = 0.3 + 1.6 * (c / (COLS - 1)) ** 1.3 * (0.7 + 0.3 * Math.sin(r * 2.1 + c * 1.3) ** 2);
                box(edges, [x - W / 2, FLOOR, z - W / 2], [x + W / 2, FLOOR + h, z + W / 2]);
            }
        }
        const pick = byLength(edges, rnd);
        const front = (ROWS - 1) / 2 * STEP + 0.45;
        return (i, out) => {
            if (i < n * 0.14) {
                const t = rnd();
                near(out, [(t - 0.5) * COLS * STEP, FLOOR + 0.55 + 1.75 * t ** 1.3 + Math.sin(t * 11) * 0.06, front], 0.012, rnd);
                out[3] = t;
            } else {
                along(out, pick(), 0.006, rnd);
                out[3] = (out[1] - FLOOR) / 2;
            }
        };
    },

    // AI · delivery: a double helix, the model learning turn by turn
    helix(n, rnd) {
        const H = 3.6, R = 0.6, TURNS = 2.25, RUNGS = 28;
        const at = (t, strand) => {
            const a = t * TURNS * TAU + strand * Math.PI;
            return [Math.cos(a) * R, (t - 0.5) * H, Math.sin(a) * R];
        };
        return (i, out) => {
            if (i < n * 0.68) {
                const t = rnd();
                near(out, at(t, i & 1), 0.022, rnd);
                out[3] = t;
            } else {
                const t = (Math.floor(rnd() * RUNGS) + 0.5) / RUNGS;
                onSegment(out, at(t, 0), at(t, 1), rnd(), 0.007, rnd);
                out[3] = t;
            }
        };
    },

    // AI · use cases: data settling into clusters around their centroids
    clusters(n, rnd) {
        const K = 6;
        const centres = Array.from({ length: K }, (_, k) => {
            const p = [0, 0, 0, 0];
            fibonacci(k, K, 1.25, p);
            return p;
        });
        const spread = centres.map(() => 0.16 + rnd() * 0.14);
        return (i, out) => {
            const k = Math.floor(rnd() * K);
            const c = centres[k];
            if (i < n * 0.08) {
                // Noise between the clusters
                out[0] = (rnd() - 0.5) * 3.2;
                out[1] = (rnd() - 0.5) * 2.6;
                out[2] = (rnd() - 0.5) * 2.6;
                out[3] = rnd();
            } else if (i < n * 0.22) {
                ring(out, spread[k] * 2.6, rnd() * TAU, k * 0.7, k * 1.3, 0.008, rnd);
                out[0] += c[0];
                out[1] += c[1];
                out[2] += c[2];
                out[3] = k / K;
            } else {
                near(out, c, spread[k] * Math.min(1, 0.4 + rnd()), rnd);
                out[3] = k / K + rnd() * 0.08;
            }
        };
    },

    // Engineering · capabilities: an architecture stack, wired layer to layer
    layers(n, rnd) {
        const LEVELS = [[-1.2, 1.5], [-0.4, 1.25], [0.4, 1.0], [1.2, 0.75]]; // [y, half width]
        const plates = [], links = [];
        LEVELS.forEach(([y, w], k) => {
            rectXZ(plates, w, y);
            [-0.5, 0, 0.5].forEach(f => {
                plates.push([[-w, y, w * f], [w, y, w * f]]);
                plates.push([[w * f, y, -w], [w * f, y, w]]);
            });
            const next = LEVELS[k + 1];
            if (!next) return;
            for (let p = 0; p < 5; p++) {
                const x = (rnd() * 2 - 1) * next[1] * 0.8, z = (rnd() * 2 - 1) * next[1] * 0.8;
                links.push([[x, y, z], [x, next[0], z]]);
            }
        });
        const pickPlate = byLength(plates, rnd), pickLink = byLength(links, rnd);
        return (i, out) => {
            if (i < n * 0.74) along(out, pickPlate(), 0.006, rnd);
            else along(out, pickLink(), 0.012, rnd);
            out[3] = (out[1] + 1.4) / 2.8;
        };
    },

    // Engineering · delivery: a circuit board, signals leaving the core along traces
    circuit(n, rnd) {
        const CHIP = 0.42, EDGE = 1.95;
        const chip = box([], [-CHIP, -0.06, -CHIP], [CHIP, 0.06, CHIP]);
        const traces = [], pads = [];
        for (let side = 0; side < 4; side++) {
            const d = [[1, 0], [0, 1], [-1, 0], [0, -1]][side];
            const l = [-d[1], d[0]];
            for (let k = 0; k < 9; k++) {
                const off = (k / 8 - 0.5) * CHIP * 1.7;
                let p = [d[0] * CHIP + l[0] * off, d[1] * CHIP + l[1] * off];
                const pts = [p];
                const go = (dx, dz, len) => pts.push(p = [p[0] + dx * len, p[1] + dz * len]);
                go(d[0], d[1], 0.12 + rnd() * 0.45);
                // Fan out diagonally, away from the middle of the side
                const turn = rnd() < 0.75 ? Math.sign(off || 1) : 0;
                go((d[0] + l[0] * turn) / Math.hypot(1, turn), (d[1] + l[1] * turn) / Math.hypot(1, turn), 0.15 + rnd() * 0.5);
                go(d[0], d[1], Math.max(0.1, EDGE * (0.72 + rnd() * 0.28) - Math.abs(p[0] * d[0] + p[1] * d[1])));
                const lengths = pts.slice(1).map((b, j) => Math.hypot(b[0] - pts[j][0], b[1] - pts[j][1]));
                const total = lengths.reduce((a, b) => a + b, 0);
                let s = 0;
                lengths.forEach((len, j) => {
                    traces.push([[pts[j][0], 0, pts[j][1]], [pts[j + 1][0], 0, pts[j + 1][1]], s / total, (s + len) / total]);
                    s += len;
                });
                pads.push([p[0], 0, p[1]]);
            }
        }
        const pickChip = byLength(chip, rnd), pickTrace = byLength(traces, rnd);
        return (i, out) => {
            if (i < n * 0.1) {
                along(out, pickChip(), 0.005, rnd);
                out[3] = 0;
            } else if (i < n * 0.16) {
                // The die: a dotted grid on top of the chip
                out[0] = (Math.floor(rnd() * 7) / 6 - 0.5) * CHIP * 1.4;
                out[1] = 0.06;
                out[2] = (Math.floor(rnd() * 7) / 6 - 0.5) * CHIP * 1.4;
                out[3] = 0;
            } else if (i < n * 0.88) {
                along(out, pickTrace(), 0.007, rnd);
            } else {
                const p = pads[Math.floor(rnd() * pads.length)];
                ring(out, 0.055, rnd() * TAU, 0, 0, 0.006, rnd);
                out[0] += p[0];
                out[2] += p[2];
                out[3] = 1;
            }
        };
    },

    // Engineering · outcomes: a hypercube, structure inside structure
    tesseract(n, rnd) {
        const corners = s => [[-s, -s, -s], [s, -s, -s], [s, -s, s], [-s, -s, s], [-s, s, -s], [s, s, -s], [s, s, s], [-s, s, s]];
        const inner = corners(0.55), outer = corners(1.3);
        const edges = [];
        box(edges, inner[0], inner[6]);
        box(edges, outer[0], outer[6]);
        inner.forEach((p, k) => edges.push([p, outer[k]]));
        const pick = byLength(edges, rnd);
        return (i, out) => {
            if (i < n * 0.1) near(out, (rnd() < 0.5 ? inner : outer)[Math.floor(rnd() * 8)], 0.04, rnd);
            else along(out, pick(), 0.006, rnd);
            out[3] = (out[1] / 1.3 + 1) / 2;
        };
    },

    // Cybersecurity · capabilities: a padlock (faces the viewer)
    lock(n, rnd) {
        const W = 0.95, TOP = 0.15, BOTTOM = -1.25, D = 0.22, R = 0.2, ARCH = 0.6, ARCH_Y = 0.6;
        const outline = [];
        [[W - R, TOP - R, 0], [-W + R, TOP - R, 1], [-W + R, BOTTOM + R, 2], [W - R, BOTTOM + R, 3]].forEach(([cx, cy, q]) => {
            for (let k = 0; k <= 6; k++) {
                const a = (q + k / 6) * Math.PI / 2;
                outline.push([cx + Math.cos(a) * R, cy + Math.sin(a) * R]);
            }
        });
        const edges = [];
        [-D, D].forEach(z => loop(edges, outline.map(([x, y]) => [x, y, z])));
        [[W, TOP - R], [-W, TOP - R], [-W, BOTTOM + R], [W, BOTTOM + R]]
            .forEach(([x, y]) => edges.push([[x, y, -D], [x, y, D]]));
        edges.push([[-0.07, -0.55, D], [-0.14, -0.95, D]], [[-0.14, -0.95, D], [0.14, -0.95, D]], [[0.14, -0.95, D], [0.07, -0.55, D]]);
        const pick = byLength(edges, rnd);
        const ARC = Math.PI * ARCH, LEG = ARCH_Y - TOP;
        return (i, out) => {
            if (i < n * 0.26) {
                // Shackle: two legs and the arch between them
                let s = rnd() * (ARC + 2 * LEG);
                let p;
                if (s < LEG) p = [-ARCH, TOP + s, 0];
                else if ((s -= LEG) < ARC) {
                    const a = Math.PI - s / ARCH;
                    p = [Math.cos(a) * ARCH, ARCH_Y + Math.sin(a) * ARCH, 0];
                } else p = [ARCH, ARCH_Y - (s - ARC), 0];
                near(out, p, 0.04, rnd);
            } else if (i < n * 0.33) {
                ring(out, 0.16, rnd() * TAU, Math.PI / 2, 0, 0.006, rnd);
                out[1] -= 0.42;
                out[2] += D;
            } else if (i < n * 0.45) {
                // Dotted face of the body
                out[0] = (Math.floor(rnd() * 13) - 6) * 0.13;
                out[1] = BOTTOM + 0.16 + Math.floor(rnd() * 9) * 0.13;
                out[2] = D;
            } else {
                along(out, pick(), 0.006, rnd);
            }
            out[3] = (out[1] - BOTTOM) / (ARCH_Y + ARCH - BOTTOM);
        };
    },

    // Cybersecurity · delivery: a radar; the phase is the bearing, so the scan sweeps around
    radar(n, rnd) {
        const blips = Array.from({ length: 7 }, () => {
            const a = rnd() * TAU, r = 0.5 + rnd() * 1.4;
            return [Math.cos(a) * r, 0, Math.sin(a) * r, a / TAU];
        });
        return (i, out) => {
            const a = rnd() * TAU;
            if (i < n * 0.5) {
                ring(out, (1 + Math.floor(rnd() * 4)) * 0.5, a, 0, 0, 0.008, rnd);
                out[3] = a / TAU;
            } else if (i < n * 0.7) {
                const k = Math.floor(rnd() * 12) / 12;
                const r = rnd() * 2;
                near(out, [Math.cos(k * TAU) * r, 0, Math.sin(k * TAU) * r], 0.006, rnd);
                out[3] = k;
            } else if (i < n * 0.92) {
                const b = blips[Math.floor(rnd() * blips.length)];
                near(out, b, 0.05, rnd);
                out[3] = b[3];
            } else {
                fibonacci(i - n * 0.92, n * 0.08, 0.12, out);
                out[3] = rnd();
            }
        };
    },

    // Cybersecurity · outcomes: a protective dome over the assets it guards
    dome(n, rnd) {
        const R = 1.75, BASE = -0.85;
        const edges = [];
        [[-0.6, -0.3], [0.45, -0.5], [0.1, 0.55], [-0.25, 0.1], [0.7, 0.35]].forEach(([x, z], k) => {
            const s = 0.14 + (k % 3) * 0.05;
            box(edges, [x - s, BASE, z - s], [x + s, BASE + s * 2 * (1 + k % 2), z + s]);
        });
        const pick = byLength(edges, rnd);
        return (i, out) => {
            const a = rnd() * TAU;
            if (i < n * 0.38) {
                const e = Math.floor(rnd() * 6) / 6 * Math.PI / 2;
                ring(out, R * Math.cos(e), a, 0, 0, 0.008, rnd);
                out[1] += BASE + R * Math.sin(e);
            } else if (i < n * 0.68) {
                const m = Math.floor(rnd() * 14) / 14 * TAU;
                const e = rnd() * Math.PI / 2;
                near(out, [Math.cos(m) * Math.cos(e) * R, BASE + Math.sin(e) * R, Math.sin(m) * Math.cos(e) * R], 0.008, rnd);
            } else if (i < n * 0.78) {
                ring(out, Math.sqrt(rnd()) * R, a, 0, 0, 0.004, rnd);
                out[1] += BASE;
            } else {
                along(out, pick(), 0.005, rnd);
            }
            out[3] = (out[1] - BASE) / R;
        };
    },

    // Talent · capabilities: a honeycomb of specialists, the raised cells are the roles you need
    honeycomb(n, rnd) {
        const S = 0.3, RINGS = 3, BASE = -0.35;
        const edges = [];
        for (let q = -RINGS; q <= RINGS; q++) {
            for (let r = -RINGS; r <= RINGS; r++) {
                if (Math.abs(q + r) > RINGS) continue;
                const cx = S * Math.sqrt(3) * (q + r / 2), cz = S * 1.5 * r;
                const d = (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2 / RINGS;
                const hex = y => Array.from({ length: 6 }, (_, k) => {
                    const a = Math.PI / 6 + k * Math.PI / 3;
                    return [cx + Math.cos(a) * S * 0.92, y, cz + Math.sin(a) * S * 0.92];
                });
                const top = BASE + (rnd() < 0.35 ? 0.15 + rnd() * 0.45 : 0);
                const upper = hex(top);
                upper.forEach((a, k) => edges.push([a, upper[(k + 1) % 6], d, d]));
                if (top === BASE) continue;
                const lower = hex(BASE);
                lower.forEach((a, k) => edges.push([a, lower[(k + 1) % 6], d, d], [a, upper[k], d, d]));
            }
        }
        const pick = byLength(edges, rnd);
        return (i, out) => along(out, pick(), 0.006, rnd);
    },

    // Talent · delivery: a funnel, from a wide search to the few who fit
    funnel(n, rnd) {
        const TOP = 1.35, NECK = -0.75, BOTTOM = -1.55;
        const radius = y => 0.22 + 1.5 * ((y - NECK) / (TOP - NECK)) ** 1.6;
        return (i, out) => {
            if (i < n * 0.42) {
                const y = NECK + Math.floor(rnd() * 9) / 8 * (TOP - NECK);
                ring(out, radius(y), rnd() * TAU, 0, 0, 0.008, rnd);
                out[1] += y;
            } else if (i < n * 0.86) {
                // Candidates spiralling down
                const t = rnd();
                const y = TOP - t * (TOP - NECK);
                const a = Math.floor(rnd() * 5) / 5 * TAU + t * 7;
                const r = radius(y) * 0.85;
                near(out, [Math.cos(a) * r, y, Math.sin(a) * r], 0.03, rnd);
            } else {
                near(out, [0, NECK - rnd() * (NECK - BOTTOM), 0], 0.05, rnd);
            }
            out[3] = (TOP - out[1]) / (TOP - BOTTOM);
        };
    },

    // Talent · outcomes: a spiral galaxy, capacity that keeps growing
    spiral(n, rnd) {
        const ARMS = 3;
        return (i, out) => {
            if (i < n * 0.16) {
                near(out, [0, 0, 0], 0.22, rnd);
                out[1] *= 0.5;
                out[3] = rnd() * 0.15;
                return;
            }
            const t = rnd() ** 0.8;
            const a = Math.floor(rnd() * ARMS) / ARMS * TAU + t * 3.6;
            const r = 0.25 + t * 1.65;
            const spread = 0.04 + t * 0.09;
            out[0] = Math.cos(a) * r + gauss(rnd) * spread;
            out[1] = gauss(rnd) * spread * 0.4;
            out[2] = Math.sin(a) * r + gauss(rnd) * spread;
            out[3] = t;
        };
    },

    // Automation · capabilities: two gears in mesh (faces the viewer); the phase
    // runs around each gear in its direction of turn, so the flow turns them
    gears(n, rnd) {
        const gear = (cx, cy, R, teeth, dir) => {
            const edges = [];
            const circle = (r, z, segments) => loop(edges, Array.from({ length: segments }, (_, k) => {
                const a = k / segments * TAU;
                const s = teeth ? Math.max(-1, Math.min(1, Math.sin(a * teeth) * 2.4)) * 0.09 : 0;
                return [cx + Math.cos(a) * (r + s), cy + Math.sin(a) * (r + s), z];
            }));
            [-0.09, 0.09].forEach(z => circle(R, z, teeth * 16));
            const plain = teeth;
            teeth = 0;
            circle(R * 0.72, 0, 64);
            circle(R * 0.22, 0, 32);
            for (let k = 0; k < 5; k++) {
                const a = k / 5 * TAU;
                edges.push([[cx + Math.cos(a) * R * 0.22, cy + Math.sin(a) * R * 0.22, 0], [cx + Math.cos(a) * R * 0.72, cy + Math.sin(a) * R * 0.72, 0]]);
            }
            return { cx, cy, dir, teeth: plain, pick: byLength(edges, rnd) };
        };
        const A = gear(-0.47, 0.32, 0.95, 12, 1);
        const pitch = 0.95 + 0.6 + 0.02;
        const B = gear(A.cx + Math.cos(-0.6) * pitch, A.cy + Math.sin(-0.6) * pitch, 0.6, 8, -1);
        return (i, out) => {
            const g = rnd() < 0.62 ? A : B;
            along(out, g.pick(), 0.006, rnd);
            const a = Math.atan2(out[1] - g.cy, out[0] - g.cx) / TAU;
            out[3] = ((g.dir * a) % 1 + 1) % 1;
        };
    },

    // Automation · delivery: Input → AI → Decision → Action → Optimise, and back again
    conveyor(n, rnd) {
        const STEPS = 5, SPAN = 3.2, NODE = 0.22;
        const nodes = [];
        for (let k = 0; k < STEPS; k++) {
            const x = (k / (STEPS - 1) - 0.5) * SPAN;
            box(nodes, [x - NODE, -NODE, -NODE], [x + NODE, NODE, NODE]);
        }
        const pick = byLength(nodes, rnd);
        // The phase runs along the line (0..0.7), then back over the return arc (0.7..1)
        return (i, out) => {
            if (i < n * 0.4) {
                along(out, pick(), 0.006, rnd);
                out[3] = (out[0] / SPAN + 0.5) * 0.7;
            } else if (i < n * 0.78) {
                const t = rnd();
                near(out, [(t - 0.5) * SPAN, 0, 0], 0.035, rnd);
                out[3] = t * 0.7;
            } else {
                const t = rnd();
                const a = t * Math.PI;
                near(out, [Math.cos(a) * SPAN / 2, Math.sin(a) * 0.35, -Math.sin(a) * 1.1], 0.025, rnd);
                out[3] = 0.7 + t * 0.3;
            }
        };
    },

    // Automation · outcomes: an infinity loop that keeps improving itself (faces the viewer)
    infinity(n, rnd) {
        const A = 1.75;
        return (i, out) => {
            const u = rnd() * TAU;
            const s = Math.sin(u), c = Math.cos(u), d = 1 + s * s;
            const strand = (i % 3 - 1) * 0.09;
            const k = 1 + strand * 0.4;
            near(out, [A * k * c / d, A * k * s * c / d * 1.2, 0.35 * s + strand], 0.028, rnd);
            out[3] = u / TAU;
        };
    },

    // Innovation · capabilities: the idea, a light bulb with a lit filament
    bulb(n, rnd) {
        const CY = 0.35, R = 1.0, NECK = -0.4, BASE = -0.75;
        return (i, out) => {
            if (i < n * 0.46) {
                // Glass: a sphere tapering into the neck
                fibonacci(i, n * 0.46, R, out);
                if (out[1] < NECK - CY) {
                    const t = rnd();
                    const a = rnd() * TAU;
                    const r = 0.42 + (0.66 - 0.42) * t * t * (3 - 2 * t);
                    out[0] = Math.cos(a) * r;
                    out[1] = BASE + t * (NECK - BASE) - CY;
                    out[2] = Math.sin(a) * r;
                }
                out[1] += CY;
            } else if (i < n * 0.64) {
                // Screw base and its tip
                const t = rnd();
                if (t < 0.85) {
                    const a = t / 0.85 * 4 * TAU;
                    near(out, [Math.cos(a) * 0.42, BASE - t / 0.85 * 0.55, Math.sin(a) * 0.42], 0.012, rnd);
                } else {
                    near(out, [0, -1.38, 0], 0.07, rnd);
                }
            } else if (i < n * 0.8) {
                // Filament: two supports and the coil between them
                const t = rnd();
                const side = rnd() < 0.5 ? -1 : 1;
                if (rnd() < 0.45) {
                    onSegment(out, [side * 0.12, BASE, 0], [side * 0.28, 0.3, 0], t, 0.006, rnd);
                } else {
                    const a = t * 9 * TAU;
                    near(out, [(t - 0.5) * 0.56, 0.3 + Math.sin(t * Math.PI) * 0.12 + Math.sin(a) * 0.05, Math.cos(a) * 0.05], 0.004, rnd);
                }
            } else if (i < n * 0.9) {
                near(out, [0, 0.32, 0], 0.28, rnd);
            } else {
                // Rays
                const a = (Math.floor(rnd() * 9) / 8 - 0.5) * Math.PI * 1.1 + Math.PI / 2;
                const r = 1.2 + rnd() * 0.2;
                near(out, [Math.cos(a) * r, CY + Math.sin(a) * r, 0], 0.01, rnd);
            }
            out[3] = (out[1] + 1.45) / 3.2;
        };
    },

    // Innovation · delivery: the double diamond (discover, define, develop,
    // deliver), facing the viewer, the flow running left to right
    diamonds(n, rnd) {
        const W = 0.95, H = 0.95;
        const edges = [];
        [-W, W].forEach(cx => [-0.1, 0.1].forEach(z => loop(edges, [[cx - W, 0, z], [cx, H, z], [cx + W, 0, z], [cx, -H, z]])));
        const pick = byLength(edges, rnd);
        return (i, out) => {
            if (i < n * 0.48) {
                along(out, pick(), 0.006, rnd);
            } else {
                // Paths that diverge and converge inside each diamond
                const cx = rnd() < 0.5 ? -W : W;
                const lane = (Math.floor(rnd() * 7) / 6 * 2 - 1) * 0.8 * H;
                const u = rnd() * 2 - 1;
                near(out, [cx + u * W, lane * (1 - u * u) ** 2, 0], 0.008, rnd);
            }
            out[3] = (out[0] / (2 * W) + 1) / 2;
        };
    },

    // Innovation · outcomes: search, a lens over the results it finds (faces the viewer)
    lens(n, rnd) {
        const CX = -0.35, CY = 0.35, R = 0.95;
        const LINES = [0.95, 1.25, 1.05, 0.7];
        return (i, out) => {
            if (i < n * 0.4) {
                const a = rnd() * TAU, b = rnd() * TAU;
                const r = R + Math.cos(b) * 0.07;
                out[0] = CX + Math.cos(a) * r;
                out[1] = CY + Math.sin(a) * r;
                out[2] = Math.sin(b) * 0.07;
            } else if (i < n * 0.62) {
                const d = [Math.SQRT1_2, -Math.SQRT1_2];
                const t = R + 0.08 + rnd() * 1.1;
                near(out, [CX + d[0] * t, CY + d[1] * t, 0], 0.07, rnd);
            } else if (i < n * 0.9) {
                // Results inside the lens; the first is the bold one
                const k = Math.floor(rnd() * LINES.length);
                near(out, [CX - 0.6 + rnd() * LINES[k], CY + 0.42 - k * 0.27, 0], k ? 0.012 : 0.028, rnd);
            } else {
                const a = Math.PI * (0.58 + rnd() * 0.3);
                near(out, [CX + Math.cos(a) * R * 0.78, CY + Math.sin(a) * R * 0.78, 0.02], 0.01, rnd);
            }
            out[3] = (out[0] + out[1]) / 4 + 0.5;
        };
    },

    // Design · capabilities: a website, its layers floating apart in depth (faces the viewer)
    browser(n, rnd) {
        const W = 1.75, H = 1.15, BAR = 0.88;
        const frame = rect([], -W, -H, W, H, 0);
        frame.push([[-W, BAR, 0], [W, BAR, 0]]);
        const layers = [];
        rect(layers, -1.55, 0.05, -0.1, 0.72, 0.18);
        rect(layers, 0.15, -0.2, 1.55, 0.72, 0.38);
        [[0.15, -0.2], [0.55, 0.25], [0.8, 0.05], [1.15, 0.42], [1.55, -0.2]].reduce((a, b) => {
            layers.push([[a[0], a[1], 0.38], [b[0], b[1], 0.38]]);
            return b;
        });
        [-1.55, -0.5, 0.55].forEach(x => rect(layers, x, -1.0, x + 1.0, -0.4, 0.26));
        const pickFrame = byLength(frame, rnd), pickLayer = byLength(layers, rnd);
        return (i, out) => {
            if (i < n * 0.28) {
                along(out, pickFrame(), 0.006, rnd);
            } else if (i < n * 0.33) {
                ring(out, 0.04, rnd() * TAU, Math.PI / 2, 0, 0.004, rnd);
                out[0] += -W + 0.16 + Math.floor(rnd() * 3) * 0.14;
                out[1] += (BAR + H) / 2;
            } else if (i < n * 0.76) {
                along(out, pickLayer(), 0.006, rnd);
            } else if (i < n * 0.92) {
                // Headline, copy and a button in the hero block
                const k = Math.floor(rnd() * 4);
                const len = [1.15, 0.9, 0.7, 0.36][k];
                near(out, [-1.4 + rnd() * len, [0.56, 0.42, 0.3, 0.16][k], 0.18], k < 2 ? 0.018 : 0.008, rnd);
            } else {
                ring(out, 0.1, rnd() * TAU, Math.PI / 2, 0, 0.006, rnd);
                out[0] += 1.2;
                out[1] += 0.5;
                out[2] += 0.38;
            }
            out[3] = (H - out[1]) / (2 * H);
        };
    },

    // Design · delivery: keyframes in sequence, a bouncing ball drawn frame by frame (faces the viewer)
    frames(n, rnd) {
        const COUNT = 6, FW = 0.62, FH = 0.36;
        const centres = Array.from({ length: COUNT }, (_, k) => {
            const t = k / (COUNT - 1) - 0.5;
            return [t * 3.0, t * 0.6, -t * 1.6];
        });
        const edges = [];
        centres.forEach((c, k) => {
            const p = k / COUNT;
            const corners = [[-FW, -FH], [FW, -FH], [FW, FH], [-FW, FH]].map(([x, y]) => [c[0] + x, c[1] + y, c[2]]);
            corners.forEach((a, j) => edges.push([a, corners[(j + 1) % 4], p, p]));
        });
        const balls = centres.map((c, k) => {
            const u = k / (COUNT - 1);
            return [c[0] + (u - 0.5) * FW * 1.2, c[1] - FH * 0.65 + Math.abs(Math.cos(u * Math.PI * 1.5)) * FH * 1.2, c[2], k / COUNT];
        });
        const path = balls.slice(1).map((b, k) => [balls[k], b, balls[k][3], b[3]]);
        const pickFrame = byLength(edges, rnd), pickPath = byLength(path, rnd);
        return (i, out) => {
            if (i < n * 0.58) {
                along(out, pickFrame(), 0.006, rnd);
            } else if (i < n * 0.84) {
                const b = balls[Math.floor(rnd() * COUNT)];
                ring(out, 0.07, rnd() * TAU, Math.PI / 2, 0, 0.006, rnd);
                out[0] += b[0];
                out[1] += b[1];
                out[2] += b[2];
                out[3] = b[3];
            } else {
                along(out, pickPath(), 0.004, rnd);
            }
        };
    },

    // Design · outcomes: a trefoil knot drawn as a wireframe tube, real-time 3D
    knot(n, rnd) {
        const S = 0.55, TUBE = 0.2, STRANDS = 8, RINGS = 90, H = 0.01;
        const curve = u => [
            S * (Math.sin(u) + 2 * Math.sin(2 * u)),
            S * (Math.cos(u) - 2 * Math.cos(2 * u)) + 0.4,
            -S * Math.sin(3 * u),
        ];
        const norm = v => {
            const l = Math.hypot(...v) || 1;
            return v.map(c => c / l);
        };
        return (i, out) => {
            const u = i < n * 0.5 ? rnd() * TAU : Math.floor(rnd() * RINGS) / RINGS * TAU;
            const v = i < n * 0.5 ? (Math.floor(rnd() * STRANDS) / STRANDS + u / TAU) * TAU : rnd() * TAU;
            const p = curve(u), a = curve(u + H), b = curve(u - H);
            const T = norm([a[0] - b[0], a[1] - b[1], a[2] - b[2]]);
            const N = norm([a[0] + b[0] - 2 * p[0], a[1] + b[1] - 2 * p[1], a[2] + b[2] - 2 * p[2]]);
            const B = [T[1] * N[2] - T[2] * N[1], T[2] * N[0] - T[0] * N[2], T[0] * N[1] - T[1] * N[0]];
            const c = Math.cos(v) * TUBE, s = Math.sin(v) * TUBE;
            near(out, [p[0] + c * N[0] + s * B[0], p[1] + c * N[1] + s * B[1], p[2] + c * N[2] + s * B[2]], 0.006, rnd);
            out[3] = u / TAU;
        };
    },
};
